/**
 * Delivery log (D-38): the support view for "I did not get the notification".
 * Every filter lives in the URL, so a support link opens the same view, including an open delivery.
 */
import {
  CHANNELS,
  type Channel,
  DELIVERY_ISSUES,
  DELIVERY_OUTCOMES,
  type DeliveryIssue,
  PROBLEM_ISSUES,
  type RetryFilter,
} from "@aiyo/shared";
import { ArrowRotateRight, ArrowsRotateRight, Funnel, ListUl } from "@gravity-ui/icons";
import { Button, Card } from "@heroui/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";
import { DeliveryDrawer } from "../components/DeliveryDrawer";
import { DeliveryTable } from "../components/DeliveryTable";
import { FilterSelect, SearchBox } from "../components/filters";
import { RetryDialog } from "../components/RetryDialog";
import { EmptyBlock, ErrorBlock, LoadingBlock, Page, PageHeader } from "../components/ui";
import { type DeliveryLogFilters, useApps, useDeliveryLog, useMe } from "../lib/queries";

const PERIODS = ["1", "7", "30"] as const;
const FILTER_KEYS = ["q", "app", "channel", "outcome", "issue", "days"] as const;

export function DeliveryLogPage() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const apps = useApps();
  const get = (k: string) => params.get(k) ?? undefined;
  const days = get("days");

  // Round to the minute so the query key stays stable between renders.
  const from = useMemo(() => {
    if (!days) return undefined;
    const now = Math.floor(Date.now() / 60_000) * 60_000;
    return new Date(now - Number(days) * 86_400_000).toISOString();
  }, [days]);

  const filters: DeliveryLogFilters = {
    q: get("q"),
    app_id: get("app"),
    channel: get("channel"),
    outcome: get("outcome"),
    issue: get("issue"),
    from,
  };
  const log = useDeliveryLog(filters);
  const rows = log.data?.pages.flatMap((p) => p.items) ?? [];
  const live = rows.some((r) => r.status === "pending" || r.status === "sending");
  const hasFilters = FILTER_KEYS.some((k) => params.has(k));
  const activeFilters = FILTER_KEYS.filter((k) => k !== "q" && params.has(k)).length;
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const { data: me } = useMe();
  // A retry covers what the filters show; the result filter does not apply, since only problems are retried.
  const retryFilter: RetryFilter = {
    ...(filters.app_id ? { app_id: filters.app_id } : {}),
    ...(filters.channel ? { channel: filters.channel as Channel } : {}),
    ...(filters.issue ? { issue: filters.issue as DeliveryIssue } : {}),
    ...(filters.q ? { q: filters.q } : {}),
    ...(from ? { from } : {}),
  };

  const update = (patch: Record<string, string | undefined>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v === undefined || v === "") next.delete(k);
      else next.set(k, v);
    }
    setParams(next, { replace: true });
  };
  const openDelivery = (id: string | null) => update({ delivery: id ?? undefined });

  return (
    <Page>
      <PageHeader
        title={t("logs.title")}
        description={t("logs.subtitle")}
        actions={
          <>
            <Button
              variant="secondary"
              isPending={log.isFetching && !log.isFetchingNextPage}
              onPress={() => log.refetch()}
            >
              <ArrowsRotateRight />
              {t("common.refresh")}
            </Button>
            {me?.role !== "viewer" ? (
              <Button onPress={() => setRetrying(true)}>
                <ArrowRotateRight />
                {t("retry.open")}
              </Button>
            ) : null}
          </>
        }
      />

      <div className="flex flex-col gap-3">
        <SearchBox
          label={t("logs.search")}
          placeholder={t("logs.searchPlaceholder")}
          value={get("q") ?? ""}
          onChange={(v) => update({ q: v })}
        />
        <Button
          variant="secondary"
          className="self-start sm:hidden"
          onPress={() => setFiltersOpen((v) => !v)}
        >
          <Funnel />
          {t("logs.filters.toggle")}
          {activeFilters ? ` (${activeFilters})` : ""}
        </Button>
        <div
          className={`flex-col gap-3 sm:flex sm:flex-row sm:flex-wrap sm:items-center ${filtersOpen ? "flex" : "hidden"}`}
        >
          <FilterSelect
            label={t("logs.filters.app")}
            allLabel={t("logs.filters.allApps")}
            value={get("app")}
            onChange={(v) => update({ app: v })}
            options={(apps.data ?? []).map((a) => ({ id: a.id, label: a.name }))}
          />
          <FilterSelect
            label={t("logs.filters.channel")}
            allLabel={t("logs.filters.allChannels")}
            value={get("channel")}
            onChange={(v) => update({ channel: v })}
            options={CHANNELS.map((c) => ({ id: c, label: t(`channels.${c}`) }))}
            className="w-full sm:w-40"
          />
          <FilterSelect
            label={t("logs.filters.outcome")}
            allLabel={t("logs.filters.allOutcomes")}
            value={get("outcome")}
            onChange={(v) => update({ outcome: v })}
            options={DELIVERY_OUTCOMES.map((o) => ({ id: o, label: t(`logs.outcomes.${o}`) }))}
            className="w-full sm:w-40"
          />
          <FilterSelect
            label={t("logs.filters.reason")}
            allLabel={t("logs.filters.anyReason")}
            value={get("issue")}
            onChange={(v) => update({ issue: v })}
            options={DELIVERY_ISSUES.filter((i) => PROBLEM_ISSUES.has(i) || i === "retrying").map((i) => ({
              id: i,
              label: t(`issues.${i}.label`),
            }))}
            className="w-full sm:w-56"
          />
          <FilterSelect
            label={t("logs.filters.period")}
            allLabel={t("logs.filters.anyTime")}
            value={days}
            onChange={(v) => update({ days: v })}
            options={PERIODS.map((p) => ({ id: p, label: t(`logs.periods.${p}`) }))}
          />
          {hasFilters ? (
            <Button
              variant="ghost"
              size="sm"
              onPress={() => update(Object.fromEntries(FILTER_KEYS.map((k) => [k, undefined])))}
            >
              {t("logs.filters.clear")}
            </Button>
          ) : null}
        </div>
      </div>

      {log.isPending ? (
        <LoadingBlock />
      ) : log.isError ? (
        <ErrorBlock error={log.error} onRetry={() => log.refetch()} />
      ) : rows.length === 0 && !hasFilters ? (
        <Card>
          <EmptyBlock icon={ListUl} title={t("logs.nothingYet")} hint={t("logs.nothingYetHint")} />
        </Card>
      ) : (
        <div
          className={`flex flex-col gap-4 transition-opacity ${log.isPlaceholderData ? "opacity-60" : ""}`}
        >
          <DeliveryTable
            rows={rows}
            label={t("logs.title")}
            onOpen={openDelivery}
            empty={
              <span className="flex flex-col gap-1">
                <span className="font-medium text-foreground">{t("logs.empty")}</span>
                <span>{t("logs.emptyHint")}</span>
              </span>
            }
          />
          {log.hasNextPage ? (
            <div className="flex justify-center">
              <Button
                variant="secondary"
                isPending={log.isFetchingNextPage}
                onPress={() => log.fetchNextPage()}
              >
                {t("common.loadMore")}
              </Button>
            </div>
          ) : null}
          {live ? <LiveRefetch refetch={log.refetch} /> : null}
        </div>
      )}

      <DeliveryDrawer deliveryId={get("delivery") ?? null} onClose={() => openDelivery(null)} />
      <RetryDialog isOpen={retrying} onOpenChange={setRetrying} selection={{ filter: retryFilter }} />
    </Page>
  );
}

/** While some rows are still in progress, refresh every few seconds. */
function LiveRefetch({ refetch }: { refetch: () => unknown }) {
  useInterval(refetch, 5_000);
  return null;
}

function useInterval(fn: () => unknown, ms: number) {
  const saved = useRef(fn);
  saved.current = fn;
  useEffect(() => {
    const id = setInterval(() => saved.current(), ms);
    return () => clearInterval(id);
  }, [ms]);
}
